import { useEffect, useState } from 'react'
import { Button, Icon } from '@/shared/ui'
import { copyToClipboard } from '@/shared/lib/clipboard'
import {
  buildErrorReport,
  classifyError,
  humanMessage,
  symbolicateStack,
} from '@/shared/lib/telemetry'
import { lastRequestId } from '@/shared/api'
import { APP } from '@/shared/config'
import s from './DeathScreen.module.css'

type DeathScreenProps = {
  error: unknown
  componentStack?: string
}

export function DeathScreen({ error, componentStack }: DeathScreenProps) {
  const [copied, setCopied] = useState(false)

  // И `error.stack`, и `componentStack` от React браузер отдаёт минифицированными
  // (см. symbolicateStack) — расшифровываем оба и пересобираем отчёт, когда результат приедет.
  // Дерево компонентов расшифровывать даже важнее: `at Cl (index.js:52:5392)` не говорит ничего,
  // а `at ChessBoard (src/widgets/chess-board/ui/ChessBoard.tsx:142:19)` говорит всё.
  //
  // Пока расшифровка идёт (или если она не удалась), отчёт остаётся с сырыми стеками — это
  // корректный запасной вариант, а не ошибка: экран смерти не имеет права ждать сеть, чтобы
  // просто появиться на экране.
  const rawStack = error instanceof Error ? error.stack : undefined
  const [decoded, setDecoded] = useState<{ stack?: string; componentStack?: string }>({})
  const [symbolicating, setSymbolicating] = useState(false)

  useEffect(() => {
    setDecoded({})
    if (!rawStack && !componentStack) return

    let cancelled = false
    setSymbolicating(true)
    // Карта бандла кешируется внутри symbolicateStack, поэтому второй вызов её не перескачивает.
    void Promise.all([
      rawStack ? symbolicateStack(rawStack) : undefined,
      componentStack ? symbolicateStack(componentStack) : undefined,
    ])
      .then(([stack, tree]) => {
        if (!cancelled) setDecoded({ stack, componentStack: tree })
      })
      .catch(() => {
        // symbolicateStack не должен падать сам — все точки отказа внутри неё перехвачены —
        // но если случится непредвиденное, экран смерти обязан молча остаться на сырых стеках,
        // а не уйти в unhandledrejection и не перезапустить сам себя через RootErrorBoundary.
      })
      .finally(() => {
        if (!cancelled) setSymbolicating(false)
      })

    return () => {
      cancelled = true
    }
  }, [rawStack, componentStack])

  const kind = classifyError(error)
  const { title, hint } = humanMessage(kind)
  const requestId = lastRequestId()
  const report = buildErrorReport({
    error,
    stack: decoded.stack,
    componentStack: decoded.componentStack ?? componentStack,
    requestId,
  })

  async function onCopy() {
    if (await copyToClipboard(report)) {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  return (
    <div className={s.root}>
      <div className={s.card}>
        <span className={s.badge} data-kind={kind}>
          <Icon name="alert" size={18} strokeWidth={1.9} />
        </span>

        <h1 className={s.title}>{title}</h1>
        <p className={s.hint}>{hint}</p>

        <div className={s.actions}>
          <Button variant="accent" onClick={() => window.location.assign('/')}>
            <Icon name="refresh" size={14} />
            Обновить страницу
          </Button>
          <Button variant="secondary" onClick={onCopy}>
            <Icon name={copied ? 'check' : 'copy'} size={14} />
            {copied ? 'Скопировано' : 'Скопировать'}
          </Button>
        </div>

        <details className={s.details}>
          <summary className={s.summary}>
            Техническая сводка
            {symbolicating ? <span className={s.pending}> · расшифровываем стек…</span> : null}
          </summary>
          <pre className={s.pre}>{report}</pre>
        </details>

        <div className={s.footer}>
          <span>
            версия {APP.version} · {APP.commit}
          </span>
          {requestId ? <span className={s.rid}>{requestId}</span> : null}
        </div>
      </div>
    </div>
  )
}
